import React, { useEffect, useState } from 'react'
import { BASE_PPI, SAFETY_MARGIN_OBJECT_NAME } from './SheetCanvas'
import { normalizeArtworkConfig } from './artworkSource'
import ArtworkSourceCard from './ArtworkSourceCard'
import ArtworkCopyList from './ArtworkCopyList'

function artworkObjects(canvas) {
  return (canvas?.getObjects?.() || []).filter(object => object.name !== SAFETY_MARGIN_OBJECT_NAME && object.uploadedImageId)
}

export default function GraphicsPanel({
  images, imageUrls, activeCanvas, placementCounts, activeSheetNumber = 1, sheetSize,
  uploadErrors = [], uploadingFiles = [], imageConfigs = {}, selectedSheetSize,
  selectedArtwork, selectedCanvasObject, onOpenUploadPicker, onUploadFiles,
  onImageConfigChange, onArtworkInstanceConfigChange, onRemoveImage, onRetryImage,
  onAddCopy, onRemoveCopy, onSelectCanvasObject, onPlaceImage,
  deletingImageIds = new Set(), onBackgroundPreview, onBackgroundApply, editingDisabled = false,
}) {
  const [isDraggingOverUpload, setIsDraggingOverUpload] = useState(false)
  const [activeSourceImageId, setActiveSourceImageId] = useState(null)
  // A new canvas selection opens its design. Expanding/collapsing a card is
  // presentation-only and must not discard the selected physical copy.
  useEffect(() => setActiveSourceImageId(null), [selectedCanvasObject])
  const placedObjects = artworkObjects(activeCanvas)
  const objectsByImage = new Map()
  placedObjects.forEach(object => {
    const key = String(object.uploadedImageId)
    const objects = objectsByImage.get(key) || []
    objects.push(object)
    objectsByImage.set(key, objects)
  })
  const selectedSourceId = activeSourceImageId ?? selectedCanvasObject?.uploadedImageId ?? selectedArtwork?.id
  const disabled = editingDisabled || activeCanvas?.selection === false
  const handleSourceToggle = image => {
    const wasExpanded = String(selectedSourceId) === String(image.id)
    setActiveSourceImageId(wasExpanded ? '' : image.id)
  }
  const handleInstanceClick = objectId => {
    const match = placedObjects.find(object => object.objectId === objectId)
    if (!match || disabled || match.selectable === false || match.evented === false) return
    setActiveSourceImageId(null)
    activeCanvas.setActiveObject(match)
    activeCanvas.requestRenderAll()
    onSelectCanvasObject?.(match)
  }
  const handlePlace = image => {
    const config = normalizeArtworkConfig(imageConfigs[image.id], image)
    if (disabled || !config.printWidthIn || !config.printHeightIn) return
    onPlaceImage?.(image.id, sheetSize.width_in * BASE_PPI / 2, sheetSize.height_in * BASE_PPI / 2, config.printWidthIn, config.printHeightIn)
  }
  const handleDragStart = (event, image) => {
    if (disabled) { event.preventDefault(); return }
    event.dataTransfer.setData('application/x-gspress-artwork', String(image.id))
    event.dataTransfer.setData('uploadedImageId', String(image.id))
  }
  const uploadDrag = (event, hovering) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDraggingOverUpload(hovering)
  }
  return (
    <div className="graphics-panel">
      {onOpenUploadPicker && onUploadFiles && (
        <div className={`graphics-panel__upload-dropzone${isDraggingOverUpload ? ' graphics-panel__upload-dropzone--drag-over' : ''}`}
          role="button" tabIndex={0} data-testid="graphics-upload-dropzone"
          onClick={onOpenUploadPicker}
          onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onOpenUploadPicker() } }}
          onDrop={event => { uploadDrag(event, false); if (event.dataTransfer?.files?.length) onUploadFiles(event.dataTransfer.files) }}
          onDragEnter={event => uploadDrag(event, true)}
          onDragOver={event => { uploadDrag(event, true); if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy' }}
          onDragLeave={event => uploadDrag(event, false)}>
          <span>Upload artwork</span><small>Drop files or click to browse</small>
        </div>
      )}
      <p className="graphics-panel__helper" data-testid="graphics-panel-helper">
        Set quantities by design. Select artwork on the sheet to edit an individual copy.
      </p>
      {uploadErrors.length > 0 && <ul className="graphics-panel__errors">{uploadErrors.map((error, index) => <li key={index}>{error}</li>)}</ul>}
      {uploadingFiles.map(file => (
        <div key={file.tempId} className="graphics-panel__uploading">
          <span>{file.filename}</span>
          <div className="graphics-panel__upload-progress-track"><div className="graphics-panel__upload-progress-bar" style={{ width: `${file.progress}%` }} /></div>
          <small>Uploading{file.progress > 0 ? ` ${file.progress}%` : '...'}</small>
        </div>
      ))}
      {images.length === 0 ? <p className="graphics-panel__empty">No images uploaded yet.</p> : (
        <section aria-label="Artwork designs">
          <div className="graphics-panel__section-heading"><span>Designs</span><span className="graphics-panel__section-count">{images.length}</span></div>
          {images.map(image => {
            const objects = objectsByImage.get(String(image.id)) || []
            return (
              <ArtworkSourceCard key={image.id} image={image} imageUrl={imageUrls[image.id]}
                config={imageConfigs[image.id]} placedCount={placementCounts?.[image.id] ?? objects.length}
                sheetCount={objects.length} sheetNumber={activeSheetNumber}
                expanded={String(selectedSourceId) === String(image.id)} onToggle={() => handleSourceToggle(image)}
                hasSelectedCopy={objects.some(object => object.objectId === selectedCanvasObject?.objectId)}
                onDragStart={event => handleDragStart(event, image)} onPlace={() => handlePlace(image)}
                onChange={onImageConfigChange} onRemove={onRemoveImage} onRetry={onRetryImage}
                deleting={deletingImageIds.has(String(image.id))} disabled={disabled}
                sheetSize={selectedSheetSize || sheetSize} onBackgroundPreview={onBackgroundPreview} onBackgroundApply={onBackgroundApply}>
                <ArtworkCopyList image={image} objects={objects} selectedObject={selectedCanvasObject}
                  sheetNumber={activeSheetNumber} sheetSize={selectedSheetSize || sheetSize} disabled={disabled}
                  onSelect={handleInstanceClick} onChange={onArtworkInstanceConfigChange} onAdd={onAddCopy} onRemove={onRemoveCopy} />
              </ArtworkSourceCard>
            )
          })}
        </section>
      )}
    </div>
  )
}
